import { createListenerMiddleware } from '@reduxjs/toolkit';
import { getCurrentSupplier, logoutUser } from '../services/authService';
import type { AuthInitialState } from '../globalSlices/authSlice';
import type { BuyersInitialState } from '../globalSlices/buyersSlice';
import type { SupplierIntegrationDetailsInitialState } from '../globalSlices/supplierIntegrationDetailsSlice';
import { resetSupplierIntegrationDetailsRootState } from '../globalSlices/supplierIntegrationDetailsSlice';
import { getSupplierIntegrationDetails } from '../services/supplierAdditionalDetailsService';
import { updateSelectedMainBuyer, resetBuyersRootState } from '../globalSlices/buyersSlice';
import {
  updateSelectedSupplierForBuyer,
  resetInvoicesRootState,
} from '../globalSlices/invoiceSlice';

// Reset actions — same list as logoutUser (authService.ts), minus resetAuthRootState
import { resetSuppliersRootState } from '../globalSlices/suppliersSlice';
import { resetSupplierJobsRootState } from '../globalSlices/supplierJobsSlice';
import { resetSupplierBuyersRootState } from '../globalSlices/supplierBuyersSlice';
import { resetSalesReceiptsRootState } from '../globalSlices/salesReceiptsSlice';
import { resetPaymentsRootState } from '../globalSlices/paymentsSlice';
import { resetJobsRootState } from '../globalSlices/jobsSlice';
import { resetLiensRootState } from '../globalSlices/liensSlice';
import { resetLienTemplatesRootState } from '../globalSlices/lienTemplatesSlice';
import { resetManualUploadsRootState } from '../globalSlices/manualUploadSlice';
import { resetGenericsRootState } from '../globalSlices/genericSlice';
import { resetInforDistSXRootState } from '../globalSlices/inforDistSX';
import { resetNoticeTemplatesRootState } from '../globalSlices/noticeTemplatesSlice';
import { resetP21RootState } from '../globalSlices/p21Slice';
import { resetPreliminaryNoticesRootState } from '../globalSlices/preliminaryNoticesSlice';
import { resetRequestedSupplierJobsRootState } from '../globalSlices/requestJobInformationSlice';
import { resetSage100RootState } from '../globalSlices/sage100Slice';
import { resetSupplierReleaseRootState } from '../globalSlices/supplierReleasesSlice';
import { resetBSubSuppliersRootState } from '../globalSlices/subSuppliersSlice';
import { resetManualUploadsRootState as resetDistributionOneRootState } from '../globalSlices/distributionOneSlice';
import { resetPaymentRequestsRootState } from '../globalSlices/paymentRequestsSlice';
import { resetP21RootState as resetReportsRootState } from '../globalSlices/reportsSlice';
import { resetFiledLiensRootState } from '../globalSlices/filedLiensSlice';
import { resetFiledLienTemplatesRootState } from '../globalSlices/filedLienTemplatesSlice';
import { resetCreditApplicationTemplatesRootState } from '../globalSlices/creditApplicationTemplatesSlice';
import { resetCreditApplicationsRootState } from '../globalSlices/creditApplicationsSlice';
import { resetCreditApplicationsMonitorRootState } from '../globalSlices/creditApplicationsMonitorSlice';
import { resetDisputesRootState } from '../globalSlices/disputesSlice';
import { resetUserLoginAttemptsRootState } from '../globalSlices/userLoginAttemptsSlice';
import { resetSyncHistoryRootState } from '../globalSlices/syncHistorySlice';
import { resetTradeReferenceState } from '../globalSlices/tradeReferenceSlice';
import { resetCollectionActivityRootState } from '../globalSlices/collectionActivitySlice';
import { resetPromiseToPayRootState } from '../globalSlices/promiseToPaySlice';
import { resetPaymentPlanRootState } from '../globalSlices/paymentPlanSlice';
import { resetInvoiceNoticeLogState } from '../globalSlices/invoiceNoticeLogSlice';
import { resetCreditSafeRootState } from '../globalSlices/creditSafeSlice';

type ListenerRootState = {
  auth: AuthInitialState;
  buyers: BuyersInitialState;
  supplierIntegrationDetails: SupplierIntegrationDetailsInitialState;
};

export const listenerMiddleware = createListenerMiddleware<ListenerRootState>();

/**
 * `integrationDetails` no longer rides along on the Supplier payload - it lives in the
 * supplierAdditionalDetails subcollection and is fetched separately, redacted. Driving that fetch
 * from `getCurrentSupplier.fulfilled` rather than from each call site is deliberate: there are
 * several places that set the active supplier (AppSetup, SupplierIndexRedirect, the supplier
 * switcher, and the `/supplier/:supplierId` deep-link effect in DashboardPage), and one that forgets
 * leaves the dashboard rendering the previous supplier's connection state. Here it cannot be missed.
 *
 * Keyed on the supplier the slice was last loaded for, so the refetches that re-read the *same*
 * supplier (CreditApplicationTemplates, GetCreditApplicationLinkModal) don't cause a second request.
 */
listenerMiddleware.startListening({
  actionCreator: getCurrentSupplier.fulfilled,
  effect: (action, listenerApi) => {
    const newSupplierId = action.payload?.data?.id;
    const loadedForSupplierId =
      listenerApi.getOriginalState().supplierIntegrationDetails.supplierId;

    if (newSupplierId && newSupplierId !== loadedForSupplierId) {
      listenerApi.dispatch(getSupplierIntegrationDetails({ supplierId: newSupplierId }));
    }
  },
});

// Lives here rather than in logoutUser's own reset list (authService.ts): this slice's reducers
// import supplierAdditionalDetailsService, which imports logoutUser, so resetting it from there
// would close an import cycle.
listenerMiddleware.startListening({
  actionCreator: logoutUser.fulfilled,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(resetSupplierIntegrationDetailsRootState());
  },
});

listenerMiddleware.startListening({
  actionCreator: getCurrentSupplier.fulfilled,
  effect: (action, listenerApi) => {
    const previousSupplierId = listenerApi.getOriginalState().auth.currentSupplier?.id;
    const newSupplierId = action.payload.data?.id;

    // Only reset if supplier actually changed (skip initial load)
    if (previousSupplierId && previousSupplierId !== newSupplierId) {
      listenerApi.dispatch(resetSuppliersRootState());
      listenerApi.dispatch(resetSupplierJobsRootState());
      listenerApi.dispatch(resetSupplierBuyersRootState());
      listenerApi.dispatch(resetInvoicesRootState());
      listenerApi.dispatch(resetSalesReceiptsRootState());
      listenerApi.dispatch(resetPaymentsRootState());
      listenerApi.dispatch(resetBuyersRootState());
      listenerApi.dispatch(resetJobsRootState());
      // listenerApi.dispatch(resetLiensRootState());
      listenerApi.dispatch(resetLienTemplatesRootState());
      listenerApi.dispatch(resetManualUploadsRootState());
      listenerApi.dispatch(resetGenericsRootState());
      listenerApi.dispatch(resetInforDistSXRootState());
      listenerApi.dispatch(resetDistributionOneRootState());
      listenerApi.dispatch(resetNoticeTemplatesRootState());
      listenerApi.dispatch(resetP21RootState());
      listenerApi.dispatch(resetPreliminaryNoticesRootState());
      listenerApi.dispatch(resetRequestedSupplierJobsRootState());
      listenerApi.dispatch(resetSage100RootState());
      listenerApi.dispatch(resetSupplierReleaseRootState());
      listenerApi.dispatch(resetBSubSuppliersRootState());
      listenerApi.dispatch(resetPaymentRequestsRootState());
      listenerApi.dispatch(resetReportsRootState());
      listenerApi.dispatch(resetFiledLiensRootState());
      listenerApi.dispatch(resetFiledLienTemplatesRootState());
      listenerApi.dispatch(resetCreditApplicationTemplatesRootState());
      listenerApi.dispatch(resetCreditApplicationsRootState());
      listenerApi.dispatch(resetCreditApplicationsMonitorRootState());
      listenerApi.dispatch(resetDisputesRootState());
      listenerApi.dispatch(resetUserLoginAttemptsRootState());
      listenerApi.dispatch(resetSyncHistoryRootState());
      listenerApi.dispatch(resetTradeReferenceState());
      listenerApi.dispatch(resetCollectionActivityRootState());
      listenerApi.dispatch(resetPromiseToPayRootState());
      listenerApi.dispatch(resetPaymentPlanRootState());
      listenerApi.dispatch(resetInvoiceNoticeLogState());
      listenerApi.dispatch(resetCreditSafeRootState());
    }
  },
});

/**
 * A buyer user with access to multiple companies switches between them via `updateSelectedMainBuyer`
 * (the company switcher in DashboardPage.tsx), which is a plain client-side navigation - no reload.
 * `selectedSupplierForBuyer` (invoiceSlice) is keyed to the PREVIOUS company's buyer/supplier
 * relationship and nothing else corrects it: pages like MainBuyerPaymentPlan only set it `if
 * (!selectedSupplierForBuyer)`, which is never true here, so every buyer-scoped fetch that derives
 * its buyerId from `selectedSupplierForBuyer` (payment plans, etc.) keeps querying the wrong company
 * - looking, from the buyer's side, like the page "sometimes doesn't load."
 */
listenerMiddleware.startListening({
  actionCreator: updateSelectedMainBuyer,
  effect: (action, listenerApi) => {
    const previousMainBuyerId = listenerApi.getOriginalState().buyers.selectedMainBuyer?.id;
    const newMainBuyerId = action.payload?.id;

    // Only reset on an actual switch between two companies - skip the initial selection (no
    // previous company yet) so first load isn't affected.
    if (previousMainBuyerId && previousMainBuyerId !== newMainBuyerId) {
      listenerApi.dispatch(updateSelectedSupplierForBuyer(null));
      listenerApi.dispatch(resetPaymentPlanRootState());
    }
  },
});
